import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  Ban,
  Banknote,
  Boxes,
  ChevronDown,
  FileInput,
  FilePenLine,
  ListChecks,
  PackageCheck,
  Pencil,
  Printer,
  Receipt,
  Send,
  Trash2,
  Truck,
  Undo2,
  Warehouse,
} from 'lucide-react';

import { cancelSalesOrder } from '#/api/crmMgmt/salesOrder/cancel';
import { deleteSalesOrder } from '#/api/crmMgmt/salesOrder/delete';
import { deliverSalesOrder } from '#/api/crmMgmt/salesOrder/deliver';
import { invoiceSalesOrder } from '#/api/crmMgmt/salesOrder/invoice';
import { receiveSalesOrderPayment } from '#/api/crmMgmt/salesOrder/receivePayment';
import { returnSalesOrderToDraft } from '#/api/crmMgmt/salesOrder/returnToDraft';
import { returnSalesOrderToPreparing } from '#/api/crmMgmt/salesOrder/returnToPreparing';
import { shipSalesOrder } from '#/api/crmMgmt/salesOrder/ship';
import { submitSalesOrder } from '#/api/crmMgmt/salesOrder/submit';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import { ActionHintIcon } from '#/modules/commonComponents/hint';
import { useCan } from '#/modules/infra/auth/useCan';
import SalesOrderExcelPrintDialog from '#/modules/crmMgmt/salesOrder/components/SalesOrderExcelPrintDialog';
import SalesOrderInvoiceDialog from '#/modules/crmMgmt/salesOrder/components/SalesOrderInvoiceDialog';
import {
  getSalesOrderApiErrorMessage,
  isSalesOrderVersionConflict,
} from '#/modules/crmMgmt/salesOrder/helper/apiError';
import { hasSalesOrderInventoryAllocation } from '#/modules/crmMgmt/salesOrder/helper/hasSalesOrderInventoryAllocation';
import {
  canAllocateSalesOrderInventory,
  canCancelSalesOrder,
  canDeleteSalesOrder,
  canDeliverSalesOrder,
  canEditSalesOrderDraft,
  canInvoiceSalesOrder,
  canReceiveSalesOrderPayment,
  canReturnSalesOrderToDraft,
  canReturnSalesOrderToPreparing,
  canShipSalesOrder,
  canShowSalesOrderStockIssue,
  canSubmitSalesOrder,
  canUpdateSalesOrderRemark,
  getSalesOrderStockIssueDetailPath,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';
import useSalesOrderExcelDownload from '#/modules/crmMgmt/salesOrder/hooks/useSalesOrderExcelDownload';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import {
  pickAvailableWorkflowExposedIds,
  resolveLgDesktopLayout,
  resolveMdDesktopLayout,
  resolveMobileLayout,
} from '#/modules/imsMgmt/receipts/shared/documentCommandBarLayout';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  salesOrderDetailQueryKey,
  salesOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const WORKFLOW_EXPOSED_ORDER = [
  'submit',
  'returnToDraft',
  'ship',
  'returnToPreparing',
  'deliver',
  'invoice',
  'receivePayment',
  'cancel',
  'stockIssue',
  'delete',
];

const ACTION_GROUPS = [
  {
    key: 'content',
    label: '單據內容',
    ids: [
      'edit',
      'editRemark',
      'allocate',
      'submit',
      'returnToDraft',
      'print',
      'printHideAmounts',
      'printSelect',
    ],
  },
  {
    key: 'fulfillment',
    label: '履約操作',
    ids: [
      'ship',
      'returnToPreparing',
      'deliver',
      'invoice',
      'receivePayment',
      'cancel',
      'stockIssue',
    ],
  },
  { key: 'status', label: '單據狀態', ids: ['delete'] },
];

const ACTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  to: PropTypes.string,
  onSelect: PropTypes.func,
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  available: PropTypes.bool,
  showExposedIcon: PropTypes.bool,
  exposedVariant: PropTypes.string,
  exposedClassName: PropTypes.string,
  tooltip: PropTypes.string,
});

function runSalesOrderCommand(id, action, payload) {
  if (action === 'submit') {
    return submitSalesOrder(id, payload);
  }
  if (action === 'returnToDraft') {
    return returnSalesOrderToDraft(id, payload);
  }
  if (action === 'ship') {
    return shipSalesOrder(id, payload);
  }
  if (action === 'returnToPreparing') {
    return returnSalesOrderToPreparing(id, payload);
  }
  if (action === 'deliver') {
    return deliverSalesOrder(id, payload);
  }
  if (action === 'invoice') {
    return invoiceSalesOrder(id, payload);
  }
  if (action === 'receivePayment') {
    return receiveSalesOrderPayment(id, payload);
  }
  if (action === 'cancel') {
    return cancelSalesOrder(id, payload);
  }
  if (action === 'delete') {
    return deleteSalesOrder(id);
  }
  throw new Error(`Unknown action: ${action}`);
}

function getExposedActionIds(actionMap) {
  return pickAvailableWorkflowExposedIds(actionMap, WORKFLOW_EXPOSED_ORDER);
}

function runAfterMenuClose(callback) {
  window.setTimeout(() => {
    callback?.();
  }, 0);
}

function renderActionLabelWithHint(action, showHint) {
  if (!showHint || !action.tooltip) {
    return action.label;
  }

  return (
    <span className="inline-flex items-center gap-0.5">
      {action.label}
      <ActionHintIcon
        tip={action.tooltip}
        ariaLabel={`${action.label}說明`}
      />
    </span>
  );
}

function SalesOrderExposedActionButton({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const showHint = disabled && Boolean(action.tooltip);
  const labelNode = renderActionLabelWithHint(action, showHint);

  if (action.to && !disabled) {
    return (
      <Button
        asChild
        variant={action.exposedVariant || 'default'}
        className={cn(action.exposedClassName)}
      >
        <Link to={action.to} aria-label={action.label}>
          {action.showExposedIcon && Icon ? (
            <Icon className="size-3.5" aria-hidden />
          ) : null}
          {action.label}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={action.destructive ? 'destructive' : (action.exposedVariant || 'default')}
      className={cn(
        action.destructive && DESTRUCTIVE_SOLID_BUTTON_CLASS,
        action.exposedClassName,
      )}
      disabled={disabled}
      onClick={() => action.onSelect?.()}
    >
      {action.showExposedIcon && Icon ? (
        <Icon className="size-3.5" aria-hidden />
      ) : null}
      {labelNode}
    </Button>
  );
}

SalesOrderExposedActionButton.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function SalesOrderMenuItem({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = Icon ? <Icon className="size-4" aria-hidden /> : null;
  const showHint = Boolean(disabled && action.tooltip);

  if (action.to && !disabled) {
    return (
      <DropdownMenuItem
        asChild
        variant={action.destructive ? 'destructive' : 'default'}
      >
        <Link to={action.to}>
          {iconNode}
          {action.label}
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      variant={action.destructive ? 'destructive' : 'default'}
      disabled={disabled && !showHint}
      className={cn(showHint && 'opacity-50')}
      onSelect={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        runAfterMenuClose(action.onSelect);
      }}
    >
      {iconNode}
      {renderActionLabelWithHint(action, showHint)}
    </DropdownMenuItem>
  );
}

SalesOrderMenuItem.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function SalesOrderActionsMenu({
  triggerLabel,
  triggerAriaLabel,
  groups,
  isPending,
}) {
  if (!groups.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          aria-label={triggerAriaLabel || triggerLabel}
        >
          {triggerLabel}
          <ChevronDown className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn('w-auto min-w-44')}>
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((action) => (
              <SalesOrderMenuItem
                key={action.id}
                action={action}
                isPending={isPending}
              />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

SalesOrderActionsMenu.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  triggerAriaLabel: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(ACTION_SHAPE).isRequired,
  })).isRequired,
  isPending: PropTypes.bool,
};

const SalesOrderCommandBar = ({ detail, onRefetch }) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [printSelectOpen, setPrintSelectOpen] = useState(false);
  const [isPrintSelectPending, setIsPrintSelectPending] = useState(false);
  const canCreate = useCan(PERM.SALES_ORDER_CREATE);
  const canUpdate = useCan(PERM.SALES_ORDER_UPDATE);
  const canReview = useCan(PERM.SALES_ORDER_REVIEW);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const status = detail?.status;
  const stockIssueId = detail?.stockIssueId;
  const stockIssuePath = getSalesOrderStockIssueDetailPath(stockIssueId);
  const excelDownload = useSalesOrderExcelDownload(id);

  async function invalidateAll() {
    await queryClient.invalidateQueries({
      queryKey: salesOrderListQueryKeyPrefix(),
    });
    if (id) {
      await queryClient.invalidateQueries({
        queryKey: salesOrderDetailQueryKey(id),
      });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    retry: false,
    mutationFn: async ({ action, payload }) => runSalesOrderCommand(id, action, payload),
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'returnToPreparing') {
        setReturnDialogOpen(false);
      }
      if (variables.action === 'invoice') {
        setInvoiceDialogOpen(false);
      }
      if (id && result?.data?.id) {
        queryClient.setQueryData(salesOrderDetailQueryKey(id), {
          message: result.message,
          route: result.route,
          data: result.data,
        });
      }
      await invalidateAll();
    },
    onError: async (error) => {
      toast.error(getSalesOrderApiErrorMessage(error));
      if (isSalesOrderVersionConflict(error)) {
        await onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending
    || excelDownload.isPrintPending
    || isPrintSelectPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交',
      message: '提交後進入備貨中，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }

  async function handleReturnToDraft() {
    const ok = await confirm({
      title: '退回草稿',
      message: '退回後恢復為草稿，確定退回？',
      confirmText: '退回草稿',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'returnToDraft', payload: { updatedAt } });
  }

  async function handleShip() {
    const ok = await confirm({
      title: '出貨',
      message: '出貨後進入出貨中，主檔將不可再改。確定出貨？',
      confirmText: '出貨',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'ship', payload: { updatedAt } });
  }

  async function handleDeliver() {
    const ok = await confirm({
      title: '送達',
      message: '送達後將建立銷售出庫單，確定送達？',
      confirmText: '送達',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'deliver', payload: { updatedAt } });
  }

  function handleReturnConfirm(reason) {
    commandMutation.mutate({
      action: 'returnToPreparing',
      payload: { updatedAt, reason },
    });
  }

  function handleInvoiceConfirm({ invoiceNo, invoicedAt, invoiceTitle, taxIdNumber }) {
    commandMutation.mutate({
      action: 'invoice',
      payload: {
        updatedAt,
        invoiceNo,
        invoicedAt,
        invoiceTitle,
        taxIdNumber,
      },
    });
  }

  async function handleReceivePayment() {
    const ok = await confirm({
      title: '收款',
      message: '確認已收款？狀態將改為已收款。',
      confirmText: '收款',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'receivePayment', payload: { updatedAt } });
  }

  async function handleCancel() {
    const ok = await confirm({
      title: '取消銷貨單',
      message: '僅草稿可取消。取消後無法復原，確定取消？',
      confirmText: '取消',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancel', payload: { updatedAt } });
  }

  async function handleDelete() {
    const ok = await confirm({
      title: '刪除銷貨單',
      message: '將軟取消此銷貨單並保留單據（狀態改為已取消）。確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'delete' });
  }

  const canShowStockIssue = canShowSalesOrderStockIssue(status, stockIssueId);
  const hasAllocation = hasSalesOrderInventoryAllocation(detail);

  const actionMap = {
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: id ? ROUTES.salesOrderEdit(id) : undefined,
      available: canUpdate && canEditSalesOrderDraft(status),
      showExposedIcon: true,
      exposedVariant: 'default',
      exposedClassName: 'document-detail-edit-action',
    },
    editRemark: {
      id: 'editRemark',
      label: '編輯備註',
      icon: FilePenLine,
      to: id ? ROUTES.salesOrderRemark(id) : undefined,
      available: canUpdate && canUpdateSalesOrderRemark(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    allocate: {
      id: 'allocate',
      label: '配置庫存',
      icon: Boxes,
      to: id ? ROUTES.salesOrderAllocate(id) : undefined,
      available: canUpdate && canAllocateSalesOrderInventory(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    submit: {
      id: 'submit',
      label: '提交',
      icon: Send,
      available: canCreate && canSubmitSalesOrder(status),
      onSelect: handleSubmit,
    },
    returnToDraft: {
      id: 'returnToDraft',
      label: '退回草稿',
      icon: FileInput,
      available: canReview && canReturnSalesOrderToDraft(status),
      onSelect: handleReturnToDraft,
    },
    print: {
      id: 'print',
      label: excelDownload.isPrintPending ? '下載中…' : '列印',
      icon: Printer,
      available: excelDownload.canPrint,
      disabled: excelDownload.isPrintPending || isPrintSelectPending,
      onSelect: () => excelDownload.handlePrint(),
    },
    printHideAmounts: {
      id: 'printHideAmounts',
      label: excelDownload.isPrintPending ? '下載中…' : '列印（隱藏金額）',
      available: excelDownload.canPrint,
      disabled: excelDownload.isPrintPending || isPrintSelectPending,
      onSelect: () => excelDownload.handlePrint({ hideAmounts: true }),
    },
    printSelect: {
      id: 'printSelect',
      label: isPrintSelectPending ? '下載中…' : '選列列印',
      icon: ListChecks,
      available: excelDownload.canPrint,
      disabled: excelDownload.isPrintPending || isPrintSelectPending,
      onSelect: () => setPrintSelectOpen(true),
    },
    ship: {
      id: 'ship',
      label: '出貨',
      icon: Truck,
      available: canReview && canShipSalesOrder(status),
      disabled: isPending || !hasAllocation,
      tooltip: !hasAllocation
        ? '請先配置庫存'
        : undefined,
      onSelect: handleShip,
    },
    returnToPreparing: {
      id: 'returnToPreparing',
      label: '退回備貨',
      icon: Undo2,
      available: canReview && canReturnSalesOrderToPreparing(status),
      onSelect: () => setReturnDialogOpen(true),
    },
    deliver: {
      id: 'deliver',
      label: '送達',
      icon: PackageCheck,
      available: canReview && canDeliverSalesOrder(status),
      onSelect: handleDeliver,
    },
    invoice: {
      id: 'invoice',
      label: status === 'INVOICED' ? '修正發票' : '開票',
      icon: Receipt,
      available: canReview && canInvoiceSalesOrder(status),
      onSelect: () => setInvoiceDialogOpen(true),
    },
    receivePayment: {
      id: 'receivePayment',
      label: '收款',
      icon: Banknote,
      available: canReview && canReceiveSalesOrderPayment(status),
      onSelect: handleReceivePayment,
    },
    cancel: {
      id: 'cancel',
      label: '取消',
      icon: Ban,
      available: canReview && canCancelSalesOrder(status),
      destructive: true,
      onSelect: handleCancel,
    },
    stockIssue: {
      id: 'stockIssue',
      label: stockIssuePath ? '出庫單' : `出庫單 ${stockIssueId || ''}`.trim(),
      icon: Warehouse,
      to: stockIssuePath || undefined,
      available: canShowStockIssue,
      disabled: canShowStockIssue && !stockIssuePath,
    },
    delete: {
      id: 'delete',
      label: '刪除',
      icon: Trash2,
      available: canCreate && canDeleteSalesOrder(status),
      destructive: true,
      onSelect: handleDelete,
    },
  };

  const mobileLayout = resolveMobileLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
  });
  const mdLayout = resolveMdDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });
  const lgLayout = resolveLgDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });

  function renderLayoutRow(
    layout,
    { triggerLabel = '更多', triggerAriaLabel = '更多操作' } = {},
  ) {
    return (
      <>
        {layout.flatActions.map((action) => (
          <SalesOrderExposedActionButton
            key={action.id}
            action={action}
            isPending={isPending}
          />
        ))}
        {layout.showMoreMenu ? (
          <SalesOrderActionsMenu
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            groups={layout.menuGroups}
            isPending={isPending}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        title="退回備貨"
        description="退回後恢復為備貨中；原因必填。確定退回？"
        confirmText="退回備貨"
        reasonLabel="退回原因"
        emptyErrorMessage="請填寫退回原因"
        isPending={isPending}
        onConfirm={handleReturnConfirm}
      />
      <SalesOrderInvoiceDialog
        open={invoiceDialogOpen}
        onOpenChange={setInvoiceDialogOpen}
        isPending={isPending}
        initialValues={{
          invoiceNo: detail?.invoiceNo ?? '',
          invoicedAt: detail?.invoicedAt ?? '',
          invoiceTitle: detail?.invoiceTitle ?? '',
          taxIdNumber: detail?.taxIdNumber ?? '',
        }}
        onConfirm={handleInvoiceConfirm}
      />
      <SalesOrderExcelPrintDialog
        open={printSelectOpen}
        onOpenChange={setPrintSelectOpen}
        salesOrderId={id}
        onPendingChange={setIsPrintSelectPending}
      />

      <div className={cn('flex items-center justify-end gap-2')}>
        <div className={cn('flex md:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mobileLayout, {
            triggerLabel: '操作',
            triggerAriaLabel: '單據操作',
          })}
        </div>
        <div className={cn('hidden md:flex lg:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mdLayout)}
        </div>
        <div className={cn('hidden lg:flex', 'items-center gap-2')}>
          {renderLayoutRow(lgLayout)}
        </div>
      </div>
    </>
  );
};

SalesOrderCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    stockIssueId: PropTypes.string,
    updatedAt: PropTypes.string,
    invoiceNo: PropTypes.string,
    invoicedAt: PropTypes.string,
    invoiceTitle: PropTypes.string,
    taxIdNumber: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default SalesOrderCommandBar;
